import { rem } from 'polished'
import { useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import type { LoyaltyLandingPageBenefitDetails } from '../LoyaltyLandingPageData'
import { getLoyaltyLandingPageBenefitCopy } from '../LoyaltyLandingPageData'
import LoyaltyLandingPageSmallTile from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageSmallTile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import Heading from 'LuxKit/Typography/Heading'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useLocation } from 'react-router'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

const bold = (chunks: React.ReactNode) => <b>{chunks}</b>

const NextTierContent = styled(Pane)`
  padding: ${rem(24)} ${rem(20)};
   /* Offset for sticky header */
  scroll-margin-top: 80px;

  ${mediaQueryUp.tablet} {
    padding: ${rem(28)} ${rem(24)};
  }
`

const SmallTilesSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

function getNextTierBenefits(
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  regionTierBenefits: App.LoyaltyTierRegionEnabledBenefits,
  intl: IntlShape,
): Record<App.LoyaltyTier, Array<LoyaltyLandingPageBenefitDetails>> {
  const loyaltyLandingPageBenefitTiles = getLoyaltyLandingPageBenefitCopy(enabledBenefits, intl)

  const moreUpgradesTitle = intl.formatMessage({
    id: '3xInpHV-OMXNljNKtpkz',
    defaultMessage: 'More upgrades to enjoy',
    description: 'Title for the hotel upgrade benefit tile on the next tier benefits section',
  })

  const silverLoungePassBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'lounge_pass')
  const goldLoungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')
  const platinumLoungePassBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'lounge_pass')

  const silverHotelUpgradeBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'hotel_upgrade')
  const goldHotelUpgradeBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'hotel_upgrade')
  const platinumHotelUpgradeBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'hotel_upgrade')

  const goldAirportTransferBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'airport_transfer')
  const platinumAirportTransferBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'airport_transfer')

  const silverDiscountedInsuranceBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'discounted_insurance')
  const goldDiscountedInsuranceBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'discounted_insurance')
  const platinumDiscountedInsuranceBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'discounted_insurance')

  const unlimitedLoungeDesc = <FormattedMessage
    id="y5WpO2UMlnhj0e4sKnrh"
    defaultMessage="Enjoy <b>unlimited lounge passes</b> to use for you and your travel companion, or keep them both for yourself."
    description="Loyalty tier benefit for unlimited lounge passes on the promotional landing page"
    values={{ b: bold }}
  />
  const additionalLoungeDesc = (count: number) => <FormattedMessage
    id="4wpsEGAQ3SheQNmDmudX"
    defaultMessage="Enjoy an additional <b>{count, plural, one {# lounge pass} other {# lounge passes}}</b> to use for you and your travel companion, or keep them both for yourself."
    description="Loyalty tier benefit for additional limited lounge passes"
    values={{ count, b: bold }}
  />
  const limitedLoungeDesc = (count: number) => <FormattedMessage
    id="LquEY1OWXkwwbsF1An7_"
    defaultMessage="Enjoy <b>{count, plural, one {# lounge pass} other {# lounge passes}}</b> to use for you and your travel companion, or keep them both for yourself."
    description="Loyalty tier benefit for limited lounge passes on the promotional landing page"
    values={{ count, b: bold }}
  />
  const unlimitedUpgradeDesc = <FormattedMessage
    id="NnoFiNEqjz0-H8jF4dDM"
    defaultMessage="Enjoy <b>unlimited</b> room or package upgrades on select offers each membership year."
    description="Loyalty tier benefit for unlimited room or package upgrades"
    values={{ b: bold }}
  />
  const additionalUpgradeDesc = (count: number) => <FormattedMessage
    id="ghdYCN_iaAqq3p-C6s5i"
    defaultMessage="Enjoy an additional <b>{count, plural, one {# room or package upgrade} other {# room or package upgrades}}</b> on select offers each membership year."
    description="Loyalty tier benefit for additional limited room or package upgrades"
    values={{ count, b: bold }}
  />
  const platinumAdditionalUpgradeDesc = (count: number) => <FormattedMessage
    id="MmrCYMz8D0_PDOuJ9ifH"
    defaultMessage="<b>Enjoy an additional {count, plural, one {# room or package upgrade} other {# room or package upgrades}}</b> on select offers each membership year."
    description="Platinum tier benefit for additional limited room or package upgrades"
    values={{ count, b: bold }}
  />
  const unlimitedFreeUpgradeDesc = <FormattedMessage
    id="nJY30gd-oGEIHhhmzIM8"
    defaultMessage="<b>Unlimited free room or package upgrades</b> on select offers each membership year."
    description="Loyalty tier benefit for unlimited hotel upgrades on the promotional landing page"
    values={{ b: bold }}
  />
  const limitedFreeUpgradeDesc = (count: number) => <FormattedMessage
    id="UqZrLo16DDmhzcZcltUJ"
    defaultMessage="<b>{count, plural, one {# free room or package upgrade} other {# free room or package upgrades}}</b> on select offers each membership year."
    description="Loyalty tier benefit for limited hotel upgrades on the promotional landing page"
    values={{ count, b: bold }}
  />
  const unlimitedTransferDesc = <FormattedMessage
    id="rozBXFnGjcsi-CNoLRWP"
    defaultMessage="Enjoy <b>unlimited private chauffeur transfers</b> in select destinations, each membership year."
    description="Loyalty tier benefit for unlimited private chauffeur transfers"
    values={{ b: bold }}
  />
  const limitedTransferDesc = (count: number) => <FormattedMessage
    id="x6hY1tRWScaDgOjcCu0m"
    defaultMessage="Enjoy <b>{count} private chauffeur transfers</b> in select destinations, each membership year."
    description="Loyalty tier benefit for limited private chauffeur transfers"
    values={{ count, b: bold }}
  />
  const insuranceWithAmountDesc = (amount: number) => <FormattedMessage
    id="9CA8LN26yl7sEQElueD4"
    defaultMessage="<b>{amount}% off</b> comprehensive travel protection, including cover for trip cancellations, medical emergencies, lost luggage and more."
    description="Discounted insurance benefit description with discount percentage"
    values={{ amount, b: bold }}
  />
  const insuranceWithoutAmountDesc = <FormattedMessage
    id="MBZ6AA9g5JL2Q4RK6fp7"
    defaultMessage="<b>Discounted</b> comprehensive travel protection, including cover for trip cancellations, medical emergencies, lost luggage and more."
    description="Discounted insurance benefit description without specific percentage"
    values={{ b: bold }}
  />
  const insuranceTitle = intl.formatMessage({
    id: 'DBIb095MUMSBZBT_zEZU',
    defaultMessage: 'Travel & Cancellation Protection',
    description: 'Title for the discounted insurance benefit tile on loyalty landing page',
  })
  const saveInsuranceDesc = (amount: number) => <FormattedMessage
    id="7F_QdeQwJpZnx2bhHKnO"
    defaultMessage="<b>Save {amount}%</b> on travel & cancellation protection"
    description="Loyalty tier discounted insurance benefit with savings percentage"
    values={{ amount, b: bold }}
  />

  const benefits = {
    bronze: [],
    silver: [
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: silverLoungePassBenefit?.unlimited
          ? unlimitedLoungeDesc
          : limitedLoungeDesc(silverLoungePassBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
      {
        title: loyaltyLandingPageBenefitTiles.hotel_upgrade?.title,
        description: silverHotelUpgradeBenefit?.unlimited
          ? unlimitedFreeUpgradeDesc
          : limitedFreeUpgradeDesc(silverHotelUpgradeBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: insuranceTitle,
        description: silverDiscountedInsuranceBenefit?.value?.amount
          ? insuranceWithAmountDesc(silverDiscountedInsuranceBenefit.value.amount)
          : insuranceWithoutAmountDesc,
        image: {
          id: '36fmxwdnmim0b53p9x7',
          title: insuranceTitle,
        },
        benefitCode: 'discounted_insurance' as App.LoyaltyBenefitType,
        enabled: enabledBenefits.discounted_insurance,
      },
    ].filter(benefit => benefit.enabled),
    gold: [
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: goldLoungePassBenefit?.unlimited
          ? unlimitedLoungeDesc
          : additionalLoungeDesc(goldLoungePassBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
      {
        title: moreUpgradesTitle,
        description: goldHotelUpgradeBenefit?.unlimited
          ? unlimitedUpgradeDesc
          : additionalUpgradeDesc(goldHotelUpgradeBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: loyaltyLandingPageBenefitTiles.airport_transfer?.title,
        description: goldAirportTransferBenefit?.unlimited
          ? unlimitedTransferDesc
          : limitedTransferDesc(goldAirportTransferBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.airport_transfer!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.airport_transfer!.benefitCode,
        enabled: enabledBenefits.airport_transfer,
      },
    ].filter(benefit => benefit.enabled),
    platinum: [
      {
        title: loyaltyLandingPageBenefitTiles.airport_transfer?.title,
        description: platinumAirportTransferBenefit?.unlimited
          ? unlimitedTransferDesc
          : limitedTransferDesc(platinumAirportTransferBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.airport_transfer!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.airport_transfer!.benefitCode,
        enabled: enabledBenefits.airport_transfer,
      },
      {
        title: moreUpgradesTitle,
        description: platinumHotelUpgradeBenefit?.unlimited
          ? unlimitedUpgradeDesc
          : platinumAdditionalUpgradeDesc(platinumHotelUpgradeBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: platinumLoungePassBenefit?.unlimited
          ? unlimitedLoungeDesc
          : additionalLoungeDesc(platinumLoungePassBenefit?.limit ?? 0),
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
    ].filter(benefit => benefit.enabled),
  }

  if (!enabledBenefits.lounge_pass && enabledBenefits.discounted_insurance) {
    benefits.gold.push({
      title: loyaltyLandingPageBenefitTiles.discounted_insurance!.title,
      description: saveInsuranceDesc(goldDiscountedInsuranceBenefit?.value?.amount ?? 0),
      image: loyaltyLandingPageBenefitTiles.discounted_insurance!.image,
      benefitCode: loyaltyLandingPageBenefitTiles.discounted_insurance!.benefitCode,
      enabled: enabledBenefits.discounted_insurance,
    })
    benefits.platinum.push({
      title: loyaltyLandingPageBenefitTiles.discounted_insurance!.title,
      description: saveInsuranceDesc(platinumDiscountedInsuranceBenefit?.value?.amount ?? 0),
      image: loyaltyLandingPageBenefitTiles.discounted_insurance!.image,
      benefitCode: loyaltyLandingPageBenefitTiles.discounted_insurance!.benefitCode,
      enabled: enabledBenefits.discounted_insurance,
    })
  }

  return benefits
}

interface Props {
  /**
   * The next tier to display the benefits for whether that is the account tier or eligible tier
   * for users not enrolled
   */
  nextTier: App.LoyaltyTier
}

function LoyaltyLandingPageNextTierBenefitsContent({ nextTier }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const intl = useIntl()
  const [config, configFetching, configError] = useLoyaltyConfig()
  const nextTierBenefits = useMemo(() => {
    if (!config) return
    return getNextTierBenefits(config.regionEnabledBenefits, config.regionTierBenefits, intl)
  }, [config, intl])

  const headingText = intl.formatMessage(
    {
      id: 'fn-eAGAOvWvTPuIyonm4',
      defaultMessage: 'Unlock {tierName} and get access to these benefits',
      description: 'Heading for the next loyalty tier benefits section',
    },
    { tierName: getLoyaltyTierDisplayName(nextTier) },
  )

  useEffect(() => {
    if (location.hash === '#benefits-your-benefits' && sectionRef.current) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash])

  if (configFetching) return <LoadingIndicator />
  if (!config || !nextTierBenefits || configError) return null

  return <NextTierContent ref={sectionRef} id="benefits-your-benefits" background={`tier-${nextTier}-primary`}>
    <VerticalSpacer gap={20}>
      <Heading variant="heading4">
        {headingText}
      </Heading>
      <SmallTilesSection>
        {nextTierBenefits[nextTier].map(benefit => <LoyaltyLandingPageSmallTile
          key={benefit.image.id}
          tile={benefit}
        />)}
      </SmallTilesSection>
    </VerticalSpacer>
  </NextTierContent>
}

export default LoyaltyLandingPageNextTierBenefitsContent
